import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory, useParams } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import Button from '../../components/Button';
import Select from '../../components/Select';
import Breadcrumb from '../../components/Breadcrumb';
import Paths from '../../constants/paths';
import translations from '../../translations/de';
import { FaSyncAlt } from 'react-icons/fa';
import {
    ALL_DELIVERY_STATUSES,
    ALL_WEBHOOK_EVENT_TYPES,
} from '../../configs/webhooks';

const statusOptions = [
    { label: translations.all || 'All', value: '' },
    ...ALL_DELIVERY_STATUSES.map((s) => ({ label: s, value: s })),
];

const eventTypeOptions = [
    { label: translations.all || 'All', value: '' },
    ...ALL_WEBHOOK_EVENT_TYPES.map((t) => ({ label: t, value: t })),
];

const Deliveries = ({ store }) => {
    const { webhook_id } = useParams();
    const history = useHistory();
    const [status, setStatus] = useState('');
    const [eventType, setEventType] = useState('');

    useEffect(() => {
        // Cancel any leftover selected subscription pointer.
        store.selectSubscription(undefined);
    }, [store]);

    useEffect(() => {
        const filters = { subscription_id: Number(webhook_id) };
        if (status) filters.status = status;
        if (eventType) filters.event_type = eventType;
        store.getDeliveries(filters);
    }, [store, webhook_id, status, eventType, store.deliveriesPage]);

    return (
        <>
            <div style={{ marginLeft: '2.5%', marginBottom: 24 }}>
                <Breadcrumb
                    name={`${translations.deliveries} #${webhook_id}`}
                    links={[{ path: Paths.WEBHOOKS, name: translations.webhooks }]}
                />
            </div>
            <div
                style={{
                    display: 'flex',
                    gap: 16,
                    padding: '0 2.5%',
                    alignItems: 'flex-end',
                    flexWrap: 'wrap',
                    marginBottom: 32,
                }}
            >
                <div style={{ minWidth: 220 }}>
                    <Select
                        label={translations.status}
                        options={statusOptions}
                        onChangeCallback={(e) => {
                            store.setDeliveriesPage(1);
                            setStatus(e ? e.value : '');
                        }}
                        hasError={false}
                    />
                </div>
                <div style={{ minWidth: 220 }}>
                    <Select
                        label={translations.event_type}
                        options={eventTypeOptions}
                        onChangeCallback={(e) => {
                            store.setDeliveriesPage(1);
                            setEventType(e ? e.value : '');
                        }}
                        hasError={false}
                    />
                </div>
                <Button
                    label={translations.refresh}
                    style={{ marginBottom: 15 }}
                    onClick={() => {
                        const filters = { subscription_id: Number(webhook_id) };
                        if (status) filters.status = status;
                        if (eventType) filters.event_type = eventType;
                        store.getDeliveries(filters);
                    }}
                >
                    <FaSyncAlt style={{ marginRight: 6 }} />
                </Button>
                <Button
                    label={translations.back_to_webhook}
                    style={{ marginBottom: 15 }}
                    onClick={() => history.push(`/webhooks/${webhook_id}`)}
                />
            </div>

            <Table
                tablePagination={
                    store.deliveriesTotalPages > 1 && (
                        <Pagination
                            totalPages={store.deliveriesTotalPages}
                            selectedPage={store.deliveriesPage}
                            onSelectedPageChange={(selected) =>
                                store.setDeliveriesPage(selected)
                            }
                        />
                    )
                }
                tableName={translations.deliveries}
                columns={[
                    { Header: 'ID', accessor: 'id', Cell: ({ value }) => <div>{value}</div> },
                    {
                        Header: translations.event_type,
                        accessor: 'event_type',
                        Cell: ({ value }) => <div>{value}</div>,
                    },
                    {
                        Header: translations.status,
                        accessor: 'status',
                        Cell: ({ value }) => <div>{value}</div>,
                    },
                    {
                        Header: translations.attempts,
                        accessor: 'attempt_count',
                        Cell: ({ value }) => <div>{value}</div>,
                    },
                    {
                        Header: translations.http_status,
                        accessor: 'last_response_status',
                        Cell: ({ value }) => <div>{value ?? '—'}</div>,
                    },
                    {
                        Header: translations.last_error,
                        accessor: 'last_error',
                        Cell: ({ value }) => (
                            <div
                                style={{
                                    maxWidth: 280,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {value ?? '—'}
                            </div>
                        ),
                    },
                    {
                        Header: translations.created_at,
                        accessor: 'created_at',
                        Cell: ({ value }) => (
                            <div>{value ? new Date(value).toLocaleString() : '—'}</div>
                        ),
                    },
                    {
                        Header: '',
                        accessor: 'action',
                        Cell: ({ row }) => {
                            const canRetry =
                                row.original.status === 'failed' ||
                                row.original.status === 'giving_up';
                            return canRetry ? (
                                <Button
                                    onClick={() => store.retryDelivery(row.cells[0].value)}
                                    title={translations.retry}
                                    label={translations.retry}
                                />
                            ) : null;
                        },
                    },
                ]}
                data={store.deliveries.map((d) => ({
                    id: d.id,
                    event_type: d.event && d.event.event_type,
                    status: d.status,
                    attempt_count: d.attempt_count,
                    last_response_status: d.last_response_status,
                    last_error: d.last_error,
                    created_at: d.created_at,
                }))}
            />
        </>
    );
};

export default observer(Deliveries);
